import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { AdditiveBlending, BufferAttribute, BufferGeometry, Color } from 'three';
import { seededRandom } from './random.js';
import { softSpriteTexture } from './softSprite.js';
import { useCelestial } from './CelestialProvider.jsx';

// Upgraded galactic swirl: dense 4-arm spiral, warm amber core → cool blue
// arms (color ramp per real galaxy palettes), additive soft-sprite points,
// core glow sprites. Particle count scales with GPU tier.
const CORE = new Color('#ffc46b');
const MID = new Color('#c9a67e');
const ARM = new Color('#7e8cc8');
const EDGE = new Color('#4a5a8a');

export function GalaxyHero({ position = [0, 0.1, -3], scale = 1 }) {
  const ref = useRef();
  const { perfTier, seeds } = useCelestial();
  const sprite = softSpriteTexture({ size: 64 });
  const glow = softSpriteTexture({
    stops: [
      [0, 'rgba(255,214,150,0.85)'],
      [0.3, 'rgba(255,196,107,0.28)'],
      [0.7, 'rgba(160,140,200,0.07)'],
      [1, 'rgba(120,110,180,0)'],
    ],
  });

  const geometry = useMemo(() => {
    const count = perfTier.whitelistHeroParticles ?? perfTier.particles;
    const random = seededRandom(seeds.galaxy);
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const tmp = new Color();
    for (let i = 0; i < count; i++) {
      const t = i / count;
      const arm = (i % 4) * (Math.PI / 2);
      const radius = Math.pow(t, 0.62) * 4.8 + random() * 0.12;
      const spread = (random() - 0.5) * (0.12 + t * 0.55);
      const angle = radius * 1.55 + arm + spread;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = Math.sin(angle) * radius * 0.36 + (random() - 0.5) * (0.3 - t * 0.14);
      positions[i * 3 + 2] = (random() - 0.5) * (0.65 - t * 0.3);

      const u = Math.min(1, radius / 4.8);
      if (u < 0.18) tmp.copy(CORE).lerp(MID, u / 0.18);
      else if (u < 0.6) tmp.copy(MID).lerp(ARM, (u - 0.18) / 0.42);
      else tmp.copy(ARM).lerp(EDGE, (u - 0.6) / 0.4);
      const dim = 0.55 + random() * 0.45;
      colors[i * 3] = tmp.r * dim;
      colors[i * 3 + 1] = tmp.g * dim;
      colors[i * 3 + 2] = tmp.b * dim;
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(positions, 3));
    geo.setAttribute('color', new BufferAttribute(colors, 3));
    return geo;
  }, [perfTier.whitelistHeroParticles, perfTier.particles, seeds.galaxy]);

  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.z = state.clock.elapsedTime * 0.03;
    ref.current.rotation.x = -0.55 + Math.sin(state.clock.elapsedTime * 0.07) * 0.03;
  });

  return (
    <group position={position} scale={scale}>
      <group ref={ref} rotation={[-0.55, 0, 0]}>
        <points geometry={geometry}>
          <pointsMaterial
            map={sprite}
            vertexColors
            size={0.06}
            sizeAttenuation
            transparent
            opacity={1}
            depthWrite={false}
            blending={AdditiveBlending}
          />
        </points>
        <sprite scale={2.6}>
          <spriteMaterial map={glow} transparent depthWrite={false} blending={AdditiveBlending} opacity={1} />
        </sprite>
        <sprite scale={5.6}>
          <spriteMaterial map={glow} transparent depthWrite={false} blending={AdditiveBlending} opacity={0.42} />
        </sprite>
      </group>
    </group>
  );
}
